import { getCreditOverage } from './credit-overage';
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { PlusCircle, Clock, AlertCircle, ArrowUpRight, Download } from 'lucide-react';
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import moment from 'moment';
import {
  fetchCreditUsage,
  exportCreditUsage,
  resetExportUrl,
  selectCreditsData,
  selectUsageData,
  selectBotUsageData,
  selectIsLoading,
  selectError,
  selectExportUrl
} from '@common-reducers/CreditUsageReducer';
import { activeSubscriptionSelector, isUserWorkSpaceOwner } from '@common-reducers/UserSelectors';
// Direct sibling imports (NOT the './' barrel) — the barrel re-exports THIS file,
// so importing siblings through it creates a circular dependency that throws
// `Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization` (TDZ) under
// the fresh build/react-refresh module-init order.
import AddCreditsModal from './add-credits-modal';
import AutoTopUpSettings from './auto-topup-settings';
import { AddCreditsSource, buildCreditsContext, useCreditsEvents } from './credits-events';
import CreditWarningAlert from './credit-warning-alert';
import { isAllAgents, notChargedCredits } from './usage-by-bot';
import { computeCreditsForecast, timeframeDays } from './credits-forecast';

// Add a safe access helper function at the beginning of the file, after imports
const getValueOrDefault = (data: any, path: string, defaultValue: any) => {
  if (!data) return defaultValue;

  const keys = path.split('.');
  let value = data;

  for (const key of keys) {
    if (value === undefined || value === null) return defaultValue;
    value = value[key];
  }

  return value === undefined || value === null ? defaultValue : value;
};

const CreditsUsageComponent: React.FC = () => {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [timeframe, setTimeframe] = useState<string>("30days");
  const [isAddCreditsModalOpen, setIsAddCreditsModalOpen] = useState<boolean>(false);
  const [addCreditsSource, setAddCreditsSource] = useState<AddCreditsSource>('unknown');
  const trackCreditsEvent = useCreditsEvents();
  const pageViewTracked = useRef(false);
  const sawLoadStart = useRef(false);

  // Use selectors to get data from the Redux store
  const creditsData = useAppSelector(selectCreditsData);
  const usageData = useAppSelector(selectUsageData);
  // #357: run-out forecast from the last 14 days of the loaded usage (null = not enough signal).
  const forecast = React.useMemo(() => computeCreditsForecast({
    usageData,
    creditsRemaining: getValueOrDefault(creditsData, 'creditsRemaining', 0),
    daysToRenewal: getValueOrDefault(creditsData, 'daysRemaining', 0),
    loadedDays: timeframeDays(timeframe),
  }), [usageData, creditsData, timeframe]);
  const botUsageData = useAppSelector(selectBotUsageData);
  // #38: agents are listed too; when they are all that's there, the section is "by agent".
  const allAgents = isAllAgents(botUsageData);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const exportUrl = useAppSelector(selectExportUrl);
  const activeSubscription = useAppSelector(activeSubscriptionSelector);
  // Members may view usage; only the workspace owner buys credits / manages the subscription.
  const isWorkSpaceOwner = useAppSelector(isUserWorkSpaceOwner);

  // Fetch credit usage data
  useEffect(() => {
    dispatch(fetchCreditUsage(timeframe));
  }, [dispatch, timeframe]);

  // #357: one credits_page_view per mount, sent once the first real payload (or an error) is in,
  // so the usage band / plan props are populated. Members are tracked too (role prop).
  useEffect(() => {
    // isLoading starts false and only flips after the fetch effect dispatches, so wait to see
    // a load begin AND settle; otherwise the first render would emit with no usage props.
    if (isLoading) { sawLoadStart.current = true; return; }
    if (pageViewTracked.current || !sawLoadStart.current) return;
    pageViewTracked.current = true;
    trackCreditsEvent('credits_page_view', buildCreditsContext({
      isOwner: isWorkSpaceOwner,
      planId: activeSubscription?.plan_id,
      creditsData,
    }));
  }, [isLoading, creditsData, error, isWorkSpaceOwner, activeSubscription, trackCreditsEvent]);

  // Debug logging in development mode
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Credits Data from Backend:', creditsData);
      console.log('Active Subscription:', activeSubscription);

      if (activeSubscription) {
        console.log('Subscription Type:', activeSubscription.type);
        console.log('Subscription Status:', activeSubscription.status);
        console.log('Subscription Plan ID:', activeSubscription.plan_id);
        console.log('Next Billing At:', activeSubscription.next_billing_at);

        // Log all entitlements for debugging
        console.log('Entitlement List:', activeSubscription.entitlementList);

        if (activeSubscription.entitlementList && activeSubscription.entitlementList.length > 0) {
          console.log('Entitlements Detail:');
          activeSubscription.entitlementList.forEach((ent: any, index: number) => {
            console.log(`Entitlement #${index + 1}:`, {
              feature_id: ent.feature_id,
              entity_type: ent.entity_type,
              entity_id: ent.entity_id,
              value: ent.value,
              name: ent.name
            });
          });
        } else {
          console.log('No entitlements found in the subscription');
        }
      } else {
        console.log('No active subscription found');
      }
    }
  }, [creditsData, activeSubscription]);

  // Handle export URL opening
  useEffect(() => {
    if (exportUrl) {
      window.open(exportUrl, '_blank');
      dispatch(resetExportUrl());
    }
  }, [exportUrl, dispatch]);

  // Check URL parameters for auto-opening add credits modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldOpenAddCredits = urlParams.get('openAddCredits') === 'true';

    if (shouldOpenAddCredits) {
      setAddCreditsSource('url_param');
      setIsAddCreditsModalOpen(true);

      // Clean up the URL parameter to avoid reopening on refresh
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('openAddCredits');
      window.history.replaceState({}, document.title, cleanUrl.pathname + cleanUrl.search);
    }
  }, []);

  // Calculate status color based on percentage
  const getStatusColor = (percentage: number): string => {
    if (percentage < 50) return "text-green-600";
    if (percentage < 80) return "text-amber-500";
    return "text-red-500";
  };

  // Get progress bar color based on percentage
  const getProgressColor = (percentage: number): string => {
    if (percentage < 50) return "bg-green-500";
    if (percentage < 80) return "bg-amber-500";
    return "bg-red-500";
  };

  // Handle export button click
  const handleExport = (): void => {
    dispatch(exportCreditUsage(timeframe));
  };

  // Handle add credits button click
  const handleAddCredits = (source: AddCreditsSource = 'unknown'): void => {
    setAddCreditsSource(source);
    setIsAddCreditsModalOpen(true);
  };

  // Get formatted next billing date using moment.js
  const getFormattedNextBillingDate = () => {
    if (activeSubscription?.next_billing_at) {
      // Convert seconds to moment object
      return moment.unix(activeSubscription.next_billing_at).format('DD/MM/YYYY');
    }
    return 'Not available';
  };

  return (
    <div className="px-4 pb-4">
      {/* Add Credits Modal */}
      {isWorkSpaceOwner && <AddCreditsModal
        open={isAddCreditsModalOpen}
        onOpenChange={setIsAddCreditsModalOpen}
        source={addCreditsSource}
      />}

      <div className="flex justify-between items-center mb-4">
        <div>
          <p style={{ color: 'var(--credits-subtitle)' }} className="text-sm">
            {t('credits.subtitle', 'Monitor your AI agent credits and usage')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex items-center gap-1 text-sm"
            onClick={handleExport}
            disabled={isLoading}
            style={{
              color: 'var(--credits-button-outline-text)',
              borderColor: 'var(--credits-button-outline-border)',
              backgroundColor: 'var(--credits-export-button-bg)'
            }}
          >
            <Download className="w-3 h-3" />
            {t('credits.exportReport', 'Export')}
          </Button>
          {isWorkSpaceOwner && <Button
            className="flex items-center gap-1 text-sm"
            onClick={() => handleAddCredits('header')}
            style={{
              backgroundColor: '#4dbdeb',
              color: 'var(--color-always-white)',
              borderColor: 'var(--credits-button-primary)'
            }}
          >
            <PlusCircle className="w-3 h-3" />
            {t('credits.addCredits', 'Add Credits')}
          </Button>}
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Credits Overview Card */}
      <Card className="mb-4 border shadow-sm" style={{
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-border)'
      }}>
        <CardContent className="p-4">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <p style={{ color: 'var(--credits-text-muted)' }}>
                {t('credits.loading', 'Loading...')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <h3 style={{ color: 'var(--credits-remaining-title)' }} className="font-medium">
                    {t('credits.remaining', 'Credits Remaining')}
                  </h3>
                  <span
                    className="font-semibold"
                    style={{
                      color: getValueOrDefault(creditsData, 'creditsRemaining', 0) === 0
                        ? 'var(--credits-remaining-value-zero)'
                        : 'var(--credits-remaining-value)'
                    }}
                  >
                    {getValueOrDefault(creditsData, 'creditsRemaining', 0)} / {getValueOrDefault(creditsData, 'totalCredits', 0)}
                  </span>
                </div>
                <div
                  className="relative h-2 w-full rounded-full overflow-hidden"
                  style={{ backgroundColor: 'var(--credits-progress-bg)' }}
                >
                  <div
                    className="absolute top-0 left-0 h-full transition-all"
                    style={{
                      width: `${getValueOrDefault(creditsData, 'percentageUsed', 0)}%`,
                      backgroundColor: getValueOrDefault(creditsData, 'percentageUsed', 0) >= 80
                        ? 'var(--credits-progress-fill-danger)'
                        : getValueOrDefault(creditsData, 'percentageUsed', 0) >= 50
                          ? 'var(--credits-progress-fill-warning)'
                          : 'var(--credits-progress-fill)'
                    }}
                  />
                </div>
                {getCreditOverage(creditsData) > 0 && (
                  <p
                    className="text-sm font-medium"
                    data-testid="credits-overage"
                    style={{ color: 'var(--credits-remaining-value-zero)' }}
                  >
                    {getCreditOverage(creditsData)} {t('credits.creditsOver', 'credits over')}
                  </p>
                )}
                <div className="flex justify-between items-center text-sm" style={{ color: 'var(--credits-text-muted)' }}>
                  <span>{getValueOrDefault(creditsData, 'percentageUsed', 0).toFixed(1)}% {t('credits.used', 'used')}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t('credits.renewsIn', 'Renews in')} {getValueOrDefault(creditsData, 'daysRemaining', 0)} {t('credits.days', 'days')}
                  </span>
                </div>
                {forecast && (
                  <p className="text-sm" data-testid="credits-forecast" style={{ color: forecast.kind === 'runs_out' ? 'var(--credits-progress-fill-warning)' : 'var(--credits-text-muted)' }}>
                    {forecast.kind === 'runs_out'
                      ? t('credits.forecastRunsOut', 'At this pace your credits last until {{date}} (about {{days}} days)', {
                          date: forecast.runOutDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }),
                          days: forecast.daysLeft,
                        })
                      : t('credits.forecastRenewsFirst', 'At this pace your credits renew before you run out')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <h3 style={{ color: 'var(--credits-remaining-title)' }} className="font-medium">
                  {t('credits.planDetails', 'Plan Details')}
                </h3>
                <p style={{ color: 'var(--credits-text-subtle)' }} className="text-sm">
                  {t('credits.renewsOn', 'Credits renew on')} {getValueOrDefault(creditsData, 'renewalDate', 'Unknown')}
                </p>
                <p style={{ color: 'var(--credits-text-subtle)' }} className="text-sm">
                  {t('credits.monthlyRenewal', 'Credits are renewed monthly regardless of billing cycle')}
                </p>
                {isWorkSpaceOwner && <div className="flex items-center mt-2">
                  <Button
                    variant="link"
                    className="p-0 h-auto flex items-center gap-1 text-sm"
                    onClick={() => handleAddCredits('manage_subscription_link')}
                    style={{
                      color: '#4dbdeb',
                      backgroundColor: 'transparent',
                      border: 'none',
                      padding: 0
                    }}
                  >
                    {t('credits.manageSubscription', 'Manage subscription')}
                    <ArrowUpRight className="w-3 h-3" />
                  </Button>
                </div>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* #357 auto top-up settings (owner only; hidden where the rollout flag is off). */}
      <AutoTopUpSettings isOwner={isWorkSpaceOwner} />

      {/* Credit Warning Alert */}
      <CreditWarningAlert
        creditsRemaining={getValueOrDefault(creditsData, 'creditsRemaining', 1)}
        percentageUsed={getValueOrDefault(creditsData, 'percentageUsed', 0)}
        isLoading={isLoading}
        onAddCredits={isWorkSpaceOwner ? () => handleAddCredits('warning_alert') : undefined}
        className="my-2"
      />

      {/* Usage Analysis Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex justify-between items-center mb-4">
          <TabsList style={{
            backgroundColor: 'var(--credits-tab-inactive-bg)',
            borderColor: 'var(--credits-card-border)'
          }}>
            <TabsTrigger
              value="overview"
              style={{
                backgroundColor: activeTab === 'overview' ? 'var(--credits-tab-active-bg)' : 'var(--credits-tab-inactive-bg)',
                color: activeTab === 'overview' ? 'var(--credits-tab-active-text)' : 'var(--credits-tab-inactive-text)'
              }}
            >
              {t('credits.overview', 'Overview')}
            </TabsTrigger>
            <TabsTrigger
              value="bybot"
              style={{
                backgroundColor: activeTab === 'bybot' ? 'var(--credits-tab-active-bg)' : 'var(--credits-tab-inactive-bg)',
                color: activeTab === 'bybot' ? 'var(--credits-tab-active-text)' : 'var(--credits-tab-inactive-text)'
              }}
            >
              {allAgents ? t('credits.byAgent', 'By agent') : t('credits.byBot', 'By Bot')}
            </TabsTrigger>
          </TabsList>

                     <Select value={timeframe} onValueChange={setTimeframe}>
             <SelectTrigger 
               /* w-auto + a floor, not a fixed 150px: that width was sized for
                  "Last 30 days" and HARD-CLIPPED the Hebrew "30 הימים האחרונים"
                  mid-word (the value span clips rather than ellipsises). */
               className="w-auto min-w-[150px]" 
               style={{
                 backgroundColor: 'var(--credits-select-bg)',
                 borderColor: 'var(--credits-select-border)',
                 color: 'var(--credits-select-text)'
               }}
             >
               <SelectValue />
             </SelectTrigger>
                         <SelectContent style={{
               backgroundColor: 'var(--credits-select-content-bg)',
               borderColor: 'var(--credits-select-content-border)'
             }}>
                               <SelectItem 
                  value="7days"
                  style={{
                    backgroundColor: 'var(--credits-select-item-bg)',
                    color: 'var(--credits-select-item-text)'
                  }}
                  className="hover:bg-opacity-90"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--credits-select-item-hover-bg)';
                    e.currentTarget.style.color = 'var(--credits-select-item-hover-text)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--credits-select-item-bg)';
                    e.currentTarget.style.color = 'var(--credits-select-item-text)';
                  }}
                >
                  {t('credits.last7Days', 'Last 7 days')}
                </SelectItem>
                               <SelectItem 
                  value="30days"
                  style={{
                    backgroundColor: 'var(--credits-select-item-bg)',
                    color: 'var(--credits-select-item-text)'
                  }}
                  className="hover:bg-opacity-90"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--credits-select-item-hover-bg)';
                    e.currentTarget.style.color = 'var(--credits-select-item-hover-text)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--credits-select-item-bg)';
                    e.currentTarget.style.color = 'var(--credits-select-item-text)';
                  }}
                >
                  {t('credits.last30Days', 'Last 30 days')}
                </SelectItem>
                               <SelectItem 
                  value="90days"
                  style={{
                    backgroundColor: 'var(--credits-select-item-bg)',
                    color: 'var(--credits-select-item-text)'
                  }}
                  className="hover:bg-opacity-90"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--credits-select-item-hover-bg)';
                    e.currentTarget.style.color = 'var(--credits-select-item-hover-text)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--credits-select-item-bg)';
                    e.currentTarget.style.color = 'var(--credits-select-item-text)';
                  }}
                >
                  {t('credits.last90Days', 'Last 90 days')}
                </SelectItem>
                               <SelectItem 
                  value="12months"
                  style={{
                    backgroundColor: 'var(--credits-select-item-bg)',
                    color: 'var(--credits-select-item-text)'
                  }}
                  className="hover:bg-opacity-90"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--credits-select-item-hover-bg)';
                    e.currentTarget.style.color = 'var(--credits-select-item-hover-text)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--credits-select-item-bg)';
                    e.currentTarget.style.color = 'var(--credits-select-item-text)';
                  }}
                >
                  {t('credits.last12Months', 'Last 12 months')}
                </SelectItem>
             </SelectContent>
          </Select>
        </div>

        <TabsContent value="overview" className="mt-0">
          <Card className="border shadow-sm" style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'var(--color-border)'
          }}>
            <CardHeader className="pb-2">
              <CardTitle style={{ color: 'var(--credits-title)' }} className="text-md font-medium">
                {t('credits.usageOverTime', 'Usage Over Time')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-60 flex items-center justify-center">
                  <p style={{ color: 'var(--credits-text-muted)' }}>
                    {t('credits.loading', 'Loading...')}
                  </p>
                </div>
              ) : usageData.length === 0 ? (
                <div className="h-60 flex items-center justify-center">
                  <p style={{ color: 'var(--credits-no-data-text)' }}>
                    {t('credits.noData', 'No usage data available for this period')}
                  </p>
                </div>
              ) : (
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={usageData}
                      margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(214.3 31.8% 91.4%)" />
                      <XAxis dataKey="name" stroke="hsl(215.4 16.3% 46.9%)" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="hsl(215.4 16.3% 46.9%)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${value}`} />
                      <Tooltip
                        formatter={(value) => [`${value} ${t('credits.credits', 'credits')}`, t('credits.usage', 'Usage')]}
                        contentStyle={{
                          borderRadius: '8px',
                          backgroundColor: 'hsl(0 0% 100%)',
                          border: '1px solid hsl(214.3 31.8% 91.4%)',
                          color: 'hsl(222.2 84% 4.9%)'
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="usage"
                        stroke="hsl(220 79% 56%)"
                        activeDot={{ r: 6 }}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bybot" className="mt-0">
          <Card className="border shadow-sm" style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'var(--color-border)'
          }}>
            <CardHeader className="pb-2">
              <CardTitle style={{ color: 'var(--credits-title)' }} className="text-md font-medium">
                {allAgents ? t('credits.usageByAgent', 'Usage by agent') : t('credits.usageByBot', 'Usage By Bot')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-60 flex items-center justify-center">
                  <p style={{ color: 'var(--credits-text-muted)' }}>
                    {t('credits.loading', 'Loading...')}
                  </p>
                </div>
              ) : botUsageData.length === 0 ? (
                <div className="h-60 flex items-center justify-center">
                  <p style={{ color: 'var(--credits-no-data-text)' }}>
                    {t('credits.noData', 'No bot usage data available for this period')}
                  </p>
                </div>
              ) : (
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={botUsageData}
                      layout="vertical"
                      margin={{ top: 5, right: 20, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(214.3 31.8% 91.4%)" horizontal={false} />
                      <XAxis type="number" stroke="hsl(215.4 16.3% 46.9%)" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        stroke="hsl(215.4 16.3% 46.9%)"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        width={100}
                      />
                      <Tooltip
                        formatter={(value, _name, item) => {
                          const notCharged = notChargedCredits(item?.payload);
                          const charged = `${value} ${t('credits.credits', 'credits')}`;
                          return [
                            notCharged ? `${charged} (${t('credits.notCharged', { count: notCharged, defaultValue: '{{count}} not charged' })})` : charged,
                            allAgents ? t('credits.usageByAgent', 'Usage by agent') : t('credits.usageByBot', 'Bot Usage'),
                          ];
                        }}
                        contentStyle={{
                          borderRadius: '8px',
                          backgroundColor: 'hsl(0 0% 100%)',
                          border: '1px solid hsl(214.3 31.8% 91.4%)',
                          color: 'hsl(222.2 84% 4.9%)'
                        }}
                      />
                      <Bar
                        dataKey="credits"
                        fill="hsl(220 79% 56%)"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Debug information moved to the bottom of the page */}
      {process.env.NODE_ENV === 'development' && (
        <div
          className="mt-8 text-xs border p-2 rounded overflow-auto"
          style={{
            backgroundColor: 'var(--credits-debug-bg)',
            borderColor: 'var(--credits-debug-border)',
            color: 'var(--credits-debug-text)'
          }}
        >
          <p><strong>Debug - Subscription:</strong> {activeSubscription ? activeSubscription.planName || activeSubscription.plan_id : "None"}</p>
          <p>
            <strong>Next Billing At:</strong> {getFormattedNextBillingDate()}
            {activeSubscription?.next_billing_at ? ` (${activeSubscription.next_billing_at})` : ''}
          </p>
          <p><strong>Credit Renewal Logic:</strong> Credits renew monthly on the same day of the month as the billing date</p>

          {/* Entitlements information */}
          {activeSubscription?.entitlementList && activeSubscription.entitlementList.length > 0 && (
            <div>
              <p><strong>Available Entitlements:</strong></p>
              <ul className="pl-4">
                {activeSubscription.entitlementList.map((ent: any, index: number) => (
                  <li key={index}>
                    {ent.feature_id || ent.name}: {ent.value || 'N/A'}
                    {ent.entity_type && ` (${ent.entity_type})`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Credit usage details */}
          <div className="mt-2 border-t pt-2">
            <p><strong>Credit Usage Data:</strong></p>
            <p>
              - Total Credits: {getValueOrDefault(creditsData, 'totalCredits', 0)}<br />
              - Credits Used: {getValueOrDefault(creditsData, 'creditsUsed', 0)}<br />
              - Credits Remaining: {getValueOrDefault(creditsData, 'creditsRemaining', 0)}<br />
              - Percentage Used: {getValueOrDefault(creditsData, 'percentageUsed', 0).toFixed(1)}%<br />
              - Renewal Date: {getValueOrDefault(creditsData, 'renewalDate', 'Unknown')}<br />
              - Days Remaining: {getValueOrDefault(creditsData, 'daysRemaining', 0)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditsUsageComponent; 